// Auth context: one source of truth for "who's logged in" + login/logout
// actions. Wraps the app once at the top so every component can call useAuth.

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

export type AuthUser = { id: string; username: string; is_admin: boolean };

type State =
  | { status: "checking" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: AuthUser };

type Ctx = {
  state: State;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: "checking" });

  // On mount: ask the backend "am I logged in?" Cookie travels automatically.
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/me", { credentials: "include" })
      .then(async (r) => {
        if (!alive) return;
        if (r.status === 200) {
          const body = (await r.json()) as { user: AuthUser };
          setState({ status: "authenticated", user: body.user });
        } else {
          setState({ status: "anonymous" });
        }
      })
      .catch(() => alive && setState({ status: "anonymous" }));
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const r = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ username, password }),
    });
    if (r.status !== 200) {
      const err = await r.json().catch(() => ({ error: r.statusText }));
      throw new Error((err as { error?: string }).error || "login failed");
    }
    const body = (await r.json()) as { user: AuthUser };
    setState({ status: "authenticated", user: body.user });
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    setState({ status: "anonymous" });
  }, []);

  return (
    <AuthContext.Provider value={{ state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): Ctx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
